import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ItemList } from '../components/items/ItemList';
import { EmptyState } from '../components/common/EmptyState';
import { CreateInspectionDialog } from '../components/inspections/CreateInspectionDialog';
import { fetchInspections, fetchProperties, fetchReportPresets, updateOnboarding } from '../lib/apiClient';
import type { InspectionStatus, Property, ReportPreset } from '../types';
import { useAuth } from '../lib/auth';

const filters: Array<{ label: string; value: InspectionStatus | 'all' }> = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'In progress', value: 'in_progress' },
  { label: 'Completed', value: 'completed' }
];

export function ItemsListPage(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<InspectionStatus | 'all'>('all');
  const [showDialog, setShowDialog] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { data: inspections = [], isLoading } = useQuery({
    queryKey: ['inspections', statusFilter, user?.organizationId],
    queryFn: () =>
      fetchInspections({
        status: statusFilter === 'all' ? undefined : statusFilter
      }),
    enabled: Boolean(user)
  });

  const { data: properties = [], isLoading: propertiesLoading } = useQuery<Property[]>({
    queryKey: ['properties', user?.organizationId],
    queryFn: fetchProperties,
    enabled: Boolean(user),
  });

  const { data: presets = [] } = useQuery<ReportPreset[]>({
    queryKey: ['report-presets'],
    queryFn: fetchReportPresets,
    enabled: Boolean(user),
  });
  const canCreate = properties.length > 0;

  useEffect(() => {
    if (searchParams.get('action') === 'new-inspection') {
      setShowDialog(true);
    }
  }, [searchParams]);

  const closeDialog = () => {
    setShowDialog(false);
    if (searchParams.get('action')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('action');
      setSearchParams(nextParams, { replace: true });
    }
  };

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return inspections;
    return inspections.filter((inspection) => inspection.status === statusFilter);
  }, [inspections, statusFilter]);

  return (
    <div className="space-y-6">
      <header className="glass-card flex flex-wrap items-center justify-between gap-4 p-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Inspection library</h2>
          <p className="text-sm text-muted-foreground">Search, filter, and manage all inspection records.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {filters.map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => setStatusFilter(filter.value)}
              className={`focus-ring rounded-full px-3 py-1.5 text-xs font-medium transition ${
                statusFilter === filter.value
                  ? 'bg-primary text-primary-foreground shadow-primary/30'
                  : 'border border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {filter.label}
            </button>
          ))}
          <button
            className="btn-glass btn-secondary focus-ring text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            type="button"
            onClick={() => {
              setSearchParams({ action: 'new-inspection' }, { replace: true });
              setShowDialog(true);
            }}
            disabled={!canCreate}
          >
            New inspection
          </button>
        </div>
      </header>

      {isLoading && <div className="glass-card h-48 animate-pulse" />}

      {!isLoading && filtered.length > 0 && <ItemList inspections={filtered} />}

      {!isLoading && filtered.length === 0 && (
        <EmptyState
          title="No inspections match this filter"
          description="Adjust filters or create a new inspection to get started."
          actionLabel="Start new inspection"
          onAction={() => {
            setSearchParams({ action: 'new-inspection' }, { replace: true });
            setShowDialog(true);
          }}
        />
      )}

      <CreateInspectionDialog
        isOpen={showDialog}
        onClose={closeDialog}
        properties={properties}
        presets={presets}
        isLoadingProperties={propertiesLoading}
        onRequestCreateProperty={() => navigate('/app/properties')}
    onCreated={async (inspectionId) => {
      await queryClient.invalidateQueries({ queryKey: ['inspections'] });
      try {
        await updateOnboarding({ step: 3, lastInspectionId: inspectionId });
        await refreshUser();
      } catch {
        // Ignore onboarding persistence errors.
      }
      closeDialog();
      navigate(`/app/items/${inspectionId}`);
    }}
  />
    </div>
  );
}
